import { useEffect, useState, useRef, useMemo } from 'react'
import * as Sentry from '@sentry/react'
import { serialize, deserialize } from './serializationUtils'
import {
  migrateToLatest,
  withVersion,
  SchemaMigrations,
} from './migrateToLatest'
import { useDebouncedState } from '@hooks/useDebouncedState'
import debounce from 'debounce'
import fastDeepEqual from 'fast-deep-equal/es6'

const saveToLocalStorage = debounce(
  <T extends object>(
    key: string,
    version: number,
    data: T,
    injectedLocalStorage = localStorage,
  ) => {
    injectedLocalStorage.setItem(
      key,
      serialize(withVersion(version, data), {
        onError: (error) => {
          Sentry.captureException(error, {
            tags: {
              operation: 'localStorage_serialize',
              key: key,
            },
            extra: {
              valueType: data ? typeof data : 'undefined',
              isArray: Array.isArray(data),
              isMap: data instanceof Map,
              isSet: data instanceof Set,
            },
          })
        },
      }),
    )
  },
  500,
)

interface UseLocalStorageOptions {
  /** Current schema version. Persisted blobs are migrated up to it on load. */
  version?: number
  /** Transformers that carry state from one version to the next. */
  migrations?: SchemaMigrations
  /** Injected to make it easier to mock and test. */
  localStorage?: Storage
}

export const useLocalStorageState = <T extends object>(
  key: string,
  initialState: T,
  {
    version = 0,
    migrations,
    localStorage: injectedLocalStorage = globalThis.localStorage,
  }: UseLocalStorageOptions = {},
) => {
  const [memoizedKey] = useState(key) // this is intentional, it's a workaround to references to a string changing on each rerender

  const { state, setState, debounced } = useDebouncedState<T>(initialState)

  const preventSave = useRef(true)

  useEffect(() => {
    preventSave.current = false // prevent saving until localstorage is loaded
    const value = injectedLocalStorage.getItem(memoizedKey)

    if (!value) return

    const persisted = deserialize<unknown>(value, {
      onError: (error) => {
        Sentry.captureException(error, {
          tags: {
            operation: 'localStorage_deserialize',
            key: memoizedKey,
          },
          extra: {
            valueLength: value?.length || 0,
            valuePreview: value?.substring(0, 100),
          },
        })
      },
    })

    // Anything already in a user's cache was written by an older release and
    // may not match the current shape, so migrate it up to the current version
    // rather than trusting it. Unparseable values migrate to initialState.
    setState(
      migrateToLatest(persisted, {
        version,
        defaults: initialState,
        migrations,
      }),
    )
  }, [memoizedKey, injectedLocalStorage]) // eslint-disable-line react-hooks/exhaustive-deps
  /**
   * I'm ignoring the missing debouncedSetState and initialState
   * dependencies above because it does not actually change
   * We want this to only run when the component mounts, essentially
   */

  return useMemo(() => {
    const clear = () => {
      injectedLocalStorage.removeItem(memoizedKey)
      setState(initialState)
    }

    const recordState = (newState: T | ((prevState: T) => T)) => {
      setState((prev = initialState) => {
        const updatedState =
          typeof newState === 'function' ? newState(prev) : newState

        if (!preventSave.current) {
          saveToLocalStorage(
            memoizedKey,
            version,
            updatedState,
            injectedLocalStorage,
          )
        }

        return updatedState
      })
    }

    const isChanged = !fastDeepEqual(state, initialState)

    return {
      state,
      debounced,
      setState: recordState,
      clear,
      isChanged,
    }
  }, [
    state,
    debounced,
    setState,
    initialState,
    memoizedKey,
    injectedLocalStorage,
    version,
  ])
}
